// i18n Configuration
// Initializes i18next with support for EN, PT, ES, IT languages

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from '../locales/en/common.json';
import ptCommon from '../locales/pt/common.json';
import esCommon from '../locales/es/common.json';
import itCommon from '../locales/it/common.json';

import enHome from '../locales/en/home.json';
import ptHome from '../locales/pt/home.json';
import esHome from '../locales/es/home.json';
import itHome from '../locales/it/home.json';

import enContact from '../locales/en/contact.json';
import ptContact from '../locales/pt/contact.json';
import esContact from '../locales/es/contact.json';
import itContact from '../locales/it/contact.json';

import enUnderConstruction from '../locales/en/underConstruction.json';
import ptUnderConstruction from '../locales/pt/underConstruction.json';
import esUnderConstruction from '../locales/es/underConstruction.json';
import itUnderConstruction from '../locales/it/underConstruction.json';

import enSale from '../locales/en/sale.json';
import ptSale from '../locales/pt/sale.json';
import esSale from '../locales/es/sale.json';
import itSale from '../locales/it/sale.json';

import enAbout from '../locales/en/about.json';
import ptAbout from '../locales/pt/about.json';
import esAbout from '../locales/es/about.json';
import itAbout from '../locales/it/about.json';

import enCatalog from '../locales/en/catalog.json';
import ptCatalog from '../locales/pt/catalog.json';
import esCatalog from '../locales/es/catalog.json';
import itCatalog from '../locales/it/catalog.json';

import enCamperDetails from '../locales/en/camperDetails.json';
import ptCamperDetails from '../locales/pt/camperDetails.json';
import esCamperDetails from '../locales/es/camperDetails.json';
import itCamperDetails from '../locales/it/camperDetails.json';

import enCheckout from '../locales/en/checkout.json';
import ptCheckout from '../locales/pt/checkout.json';
import esCheckout from '../locales/es/checkout.json';
import itCheckout from '../locales/it/checkout.json';

import enFaq from '../locales/en/faq.json';
import ptFaq from '../locales/pt/faq.json';
import esFaq from '../locales/es/faq.json';
import itFaq from '../locales/it/faq.json';

import enCookie from '../locales/en/cookie.json';
import ptCookie from '../locales/pt/cookie.json';
import esCookie from '../locales/es/cookie.json';
import itCookie from '../locales/it/cookie.json';

import enLegal from '../locales/en/legal.json';
import ptLegal from '../locales/pt/legal.json';
import esLegal from '../locales/es/legal.json';
import itLegal from '../locales/it/legal.json';

import enWhatsIncluded from '../locales/en/whatsIncluded.json';
import ptWhatsIncluded from '../locales/pt/whatsIncluded.json';
import esWhatsIncluded from '../locales/es/whatsIncluded.json';
import itWhatsIncluded from '../locales/it/whatsIncluded.json';

// French — every namespace is translated, so nothing falls back to EN
import frCommon from '../locales/fr/common.json';
import frHome from '../locales/fr/home.json';
import frContact from '../locales/fr/contact.json';
import frUnderConstruction from '../locales/fr/underConstruction.json';
import frSale from '../locales/fr/sale.json';
import frAbout from '../locales/fr/about.json';
import frCatalog from '../locales/fr/catalog.json';
import frCamperDetails from '../locales/fr/camperDetails.json';
import frCheckout from '../locales/fr/checkout.json';
import frFaq from '../locales/fr/faq.json';
import frCookie from '../locales/fr/cookie.json';
import frLegal from '../locales/fr/legal.json';
import frWhatsIncluded from '../locales/fr/whatsIncluded.json';

// Surf page
import enSurf from '../locales/en/surf.json';
import ptSurf from '../locales/pt/surf.json';
import esSurf from '../locales/es/surf.json';
import itSurf from '../locales/it/surf.json';
import frSurf from '../locales/fr/surf.json';

// Configure supported languages
const SUPPORTED_LANGUAGES = ['en', 'pt', 'es', 'it', 'fr'];
const DEFAULT_LANGUAGE = 'en';

// Language detection configuration
const detectionOptions = {
  // Order of detection
  order: ['path', 'localStorage', 'navigator'],

  // Look for language in URL path
  lookupFromPathIndex: 0,

  // Cache user language preference
  caches: ['localStorage'],

  // Don't use cookies for GDPR compliance
  excludeCacheFor: ['cimode'],
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources: {
      en: {
        common: enCommon,
        home: enHome,
        contact: enContact,
        underConstruction: enUnderConstruction,
        sale: enSale,
        about: enAbout,
        catalog: enCatalog,
        camperDetails: enCamperDetails,
        checkout: enCheckout,
        faq: enFaq,
        cookie: enCookie,
        legal: enLegal,
        whatsIncluded: enWhatsIncluded,
        surf: enSurf,
      },
      pt: {
        common: ptCommon,
        home: ptHome,
        contact: ptContact,
        underConstruction: ptUnderConstruction,
        sale: ptSale,
        about: ptAbout,
        catalog: ptCatalog,
        camperDetails: ptCamperDetails,
        checkout: ptCheckout,
        faq: ptFaq,
        cookie: ptCookie,
        legal: ptLegal,
        whatsIncluded: ptWhatsIncluded,
        surf: ptSurf,
      },
      es: {
        common: esCommon,
        home: esHome,
        contact: esContact,
        underConstruction: esUnderConstruction,
        sale: esSale,
        about: esAbout,
        catalog: esCatalog,
        camperDetails: esCamperDetails,
        checkout: esCheckout,
        faq: esFaq,
        cookie: esCookie,
        legal: esLegal,
        whatsIncluded: esWhatsIncluded,
        surf: esSurf,
      },
      it: {
        common: itCommon,
        home: itHome,
        contact: itContact,
        underConstruction: itUnderConstruction,
        sale: itSale,
        about: itAbout,
        catalog: itCatalog,
        camperDetails: itCamperDetails,
        checkout: itCheckout,
        faq: itFaq,
        cookie: itCookie,
        legal: itLegal,
        whatsIncluded: itWhatsIncluded,
        surf: itSurf,
      },
      fr: {
        common: frCommon,
        home: frHome,
        contact: frContact,
        underConstruction: frUnderConstruction,
        sale: frSale,
        about: frAbout,
        catalog: frCatalog,
        camperDetails: frCamperDetails,
        checkout: frCheckout,
        faq: frFaq,
        cookie: frCookie,
        legal: frLegal,
        whatsIncluded: frWhatsIncluded,
        surf: frSurf,
      },
    },

    // Supported languages
    supportedLngs: SUPPORTED_LANGUAGES,

    // Fallback language
    fallbackLng: DEFAULT_LANGUAGE,

    // Default namespace
    defaultNS: 'common',

    // Language detection
    detection: detectionOptions,

    // Debugging (disable in production)
    debug: process.env.NODE_ENV === 'development',

    // Interpolation options
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React specific options
    react: {
      useSuspense: true, // Enable suspense mode
    },
  });

export default i18n;
export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };
